import { useEffect, useState } from "react";
import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import layoutConf from "../../config/layoutConf";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import AccessControl from "../functional/AccessControl";
import notificationState from "../../states/notificationState";
import CreateRecordButton from "../input/CreateRecordButton";
import UpdateRecordButton from "../input/UpdateRecordButton";
import DeleteSingleRecordButton from "../input/DeleteSingleRecordButton";
import getSchema from "../../lib/api/getSchema";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import GridActionButton from "../input/GridActionButton";
import CustomSelector from "../input/CustomSelector";
import apiClient from "../../lib/common/apiClient";
import ItemEmbed from "./ItemEmbed";

/**
 * @COMPONENT_NAME ItemColor
 * @DESCRIPTION 제품 색상 관리를 위한 컴포넌트 화면입니다. 제품 그리드에서 선택된 제품에 대한 색상 코드와 공정 코드를 지정할 수 있습니다.
 *
 * @PROPS
 *   - (props 없음, 라우트/상위에서 직접 사용)
 *
 * @STATE
 *   - {string} selectedTargetName - 선택된 전/후면 코드 이름
 *   - {string} selectedTargetCode - 선택된 전/후면 코드
 *   - {object} selectedItemColor - 선택된 제품 색상 정보
 *   - {object} itemSelected - 선택된 제품 정보
 *   - {boolean} enableMoveUp - 위로 이동 버튼 활성화 여부
 *   - {boolean} enableMoveDown - 아래로 이동 버튼 활성화 여부
 *   - {boolean} disableDelButton - 삭제 버튼 비활성화 여부
 *   - {array}  apiArray - 전/후면 key, value 객체를 리스트화 한 데이터
 *   - {object} schema - 제품 색상 스키마
 *   - {object} api - 제품 색상 API 인스턴스
 *   - {object} grid - 제품 색상 그리드 인스턴스
 *   - 기타 내부 상태
 *
 * @PASSTHROUGH_PROPS
	- {function} onModifyViewUrl - 뷰 URL 수정 함수
	- {function} setInitialDataCallback - 초기 폼 데이터 설정 콜백함수
	- {function} onInitialized - 그리드 초기화 함수
	- {function} onApiInitialized - API 초기화 함수
 */

function ItemColor(props) {
	const table = "ItemColor";
	const view = "VwItemColor";
	const [height, setHeight] = useState(0);
	const [grid, setGrid] = useState();
	const [api, setApi] = useState();
	const [selectedItemColor, setSelectedItemColor] = useState();
	const [selectedItem, setSelectedItem] = useState();
	const [schema, setSchema] = useState();
	const [enableMoveUp, setEnableMoveUp] = useState(false);
	const [enableMoveDown, setEnableMoveDown] = useState(false);
	const [apiArray, setApiArray] = useState([]);
	const [selectedTargetName, setSelectedTargetName] = useState();
	const [selectedTargetCode, setSelectedTargetCode] = useState();
	const [disableDelButton, setDisableDelButton] = useState(false);
	const handleResize = () => {
		setHeight(document.documentElement.offsetHeight - 96);
	};
	const { t } = useTranslation();
	const setNotification = useSetRecoilState(notificationState);
	useEffect(() => {
		(async () => {
			let response = await apiClient.get("/api/VwCommonCodeSlave/P061");
			const list = response?.data?.data;
			if (Array.isArray(list)) {
				// [{ name, code }] 형태로 변환
				const selected = list.map((item) => ({
					name: item?.slaveCodeName,
					code: item?.slaveCodeId,
				}));
				setApiArray(selected);
				setSelectedTargetName(selected[0]?.name);
			} else {
				setApiArray([]);
			}
		})();
	}, []);
	useEffect(() => {
		window.addEventListener("resize", handleResize);
		handleResize();
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		let mounted = true;

		async function schemaSetting() {
			let schema = await getSchema(table);
			if (mounted) {
				setSchema(schema);
			}
		}

		schemaSetting();
		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		if (grid) {
			grid.selectionChanged.addHandler((grid, event) => {
				if (grid.selectedItems && grid.selectedItems[0]) {
					setDisableDelButton(false);
					setSelectedItemColor(grid.selectedItems[0]);
					if (grid.selectedItems[0].procSeq === 1) {
						setEnableMoveUp(false);
					} else {
						setEnableMoveUp(true);
					}

					if (grid.selectedItems[0].procSeq === grid.rows.length) {
						setEnableMoveDown(false);
					} else {
						setEnableMoveDown(true);
					}
				} else {
					setSelectedItemColor();
					setEnableMoveUp(false);
					setEnableMoveDown(false);
					setDisableDelButton(true);
				}
			});

			grid.itemsSourceChanged.addHandler((grid, event) => {
				if (grid.selectedItems && grid.selectedItems[0]) {
					setDisableDelButton(false);
					setSelectedItemColor(grid.selectedItems[0]);
					if (grid.selectedItems[0].procSeq === 1) {
						setEnableMoveUp(false);
					} else {
						setEnableMoveUp(true);
					}

					if (grid.selectedItems[0].procSeq === grid.rows.length) {
						setEnableMoveDown(false);
					} else {
						setEnableMoveDown(true);
					}
				} else {
					setSelectedItemColor();
					setDisableDelButton(true);
					setEnableMoveUp(false);
					setEnableMoveDown(false);
				}
			});
		}
	}, [grid]);

	useEffect(() => {
		setApi((api) => {
			if (api) {
				api.refresh();
			}

			return api;
		});
	}, [selectedItem, selectedTargetName]);

	const handleItemSelect = (item) => {
		setSelectedItem(item && item.length !== 0 ? item : null);
	};

	const onApiInitialized = (api) => {
		setApi(api);
	};

	const onGridInitialized = (grid) => {
		grid.allowSorting = false; // disable sorting - must be ordered by process order
		grid.selectionMode = "Row";

		setGrid(grid);
	};

	const notifySuccess = (msg) => {
		setNotification({
			open: true,
			severity: "success",
			msg: msg,
		});
	};

	const notifyFailure = (msg) => {
		setNotification({
			open: true,
			severity: "error",
			msg: msg,
		});
	};

	const onSuccess = (msg) => {
		notifySuccess(msg);
		if (api) {
			api.refresh();
		}
	};

	const onFailure = (reason) => {
		notifyFailure(reason);
	};

	const setMoveUpMsg = (grid) => {
		return t("dialog.msg.confirm.moveUpProcess", {
			process: `${grid.selectedItems[0].processName}[${grid.selectedItems[0].processCode}]`,
		});
	};
	const setMoveUpUrl = (grid) => {
		return `/api/CardColorMove/moveup/${table}/${grid.selectedItems[0].cardUserCd}/${grid.selectedItems[0].cardClass}/${grid.selectedItems[0].frontRearCode}/${grid.selectedItems[0].procSeq}`;
	};
	const onSuccessMoveUp = (grid, api) => {
		api.refresh();
		onSuccess(
			t("success.moveUpProcess", {
				processCode: grid.selectedItems[0].processCode,
				processName: grid.selectedItems[0].processName,
			})
		);
	};
	const setMoveDownMsg = (grid) => {
		return t("dialog.msg.confirm.moveDownProcess", {
			process: `${grid.selectedItems[0].processName}[${grid.selectedItems[0].processCode}]`,
		});
	};
	const setMoveDownUrl = (grid) => {
		return `/api/CardColorMove/movedown/${table}/${grid.selectedItems[0].cardUserCd}/${grid.selectedItems[0].cardClass}/${grid.selectedItems[0].frontRearCode}/${grid.selectedItems[0].procSeq}`;
	};
	const onSuccessMoveDown = (grid, api) => {
		api.refresh();
		onSuccess(
			t("success.moveDownProcess", {
				processCode: grid.selectedItems[0].processCode,
				processName: grid.selectedItems[0].processName,
			})
		);
	};
	const makeItemName = (row) => {
		return `${row.processName}[${row.processCode}]`;
	};
	const onModifyViewUrl = (url) => {
		const selected = apiArray.find(
			(item) => item.name === selectedTargetName
		);
		const code = selected?.code;
		if (code) {
			setSelectedTargetCode(code);
		}
		let newUrl = `${url}/${selectedItem?.materialCode}/${selectedItem?.materialCodeVer}/${code}`;
		return newUrl;
	};

	return (
		<div style={{ height: height }}>
			<ReflexContainer orientation="vertical">
				<ReflexElement flex={0.33} data-cy="ItemEmbed">
					<ItemEmbed id="ItemEmbed" onSelect={handleItemSelect} />
				</ReflexElement>
				<ReflexSplitter
					style={{ width: layoutConf.reflex.splitterWidth }}
				/>
				<ReflexElement flex={0.67} data-cy="ItemColor">
					<BasicCrudScreenType
						id={table}
						headerTitle={t("term.itemColor")}
						table={table}
						view={view}
						onModifyViewUrl={onModifyViewUrl}
						onInitialized={onGridInitialized}
						onApiInitialized={onApiInitialized}
						noCreate
						noEdit
						noDelete
						noExcel
						fixedOrder={[{ column: "procSeq", order: "ASC" }]}
						onAppendButtonInFrontOfSearch={() => (
							<>
								<BasicCrudScreenActionItemLayout>
									<CustomSelector
										value={selectedTargetName}
										setValue={setSelectedTargetName}
										apiArray={apiArray.map(
											(item) => item.name
										)}
										width={"90px"}
									/>
								</BasicCrudScreenActionItemLayout>
								<BasicCrudScreenActionItemLayout>
									<GridActionButton
										id="moveUpSequence-moveup"
										disabled={!enableMoveUp}
										setMsg={() => setMoveUpMsg(grid)}
										setUrl={() => setMoveUpUrl(grid)}
										onSuccess={() =>
											onSuccessMoveUp(grid, api)
										}
										onFailure={onFailure}
										title={t("buttonName.moveUpSequence")}
									/>
								</BasicCrudScreenActionItemLayout>
								<BasicCrudScreenActionItemLayout>
									<GridActionButton
										id="moveDownSequence-movedown"
										disabled={!enableMoveDown}
										setMsg={() => setMoveDownMsg(grid)}
										setUrl={() => setMoveDownUrl(grid)}
										onSuccess={() =>
											onSuccessMoveDown(grid, api)
										}
										onFailure={onFailure}
										title={t("buttonName.moveDownSequence")}
									/>
								</BasicCrudScreenActionItemLayout>
								<BasicCrudScreenActionItemLayout>
									<AccessControl
										accessTarget={schema && schema.name}
										accessMethod="POST"
									>
										<CreateRecordButton
											id={table}
											schema={schema}
											refresh={api}
											disabled={!selectedItem}
											setInitialDataCallback={(
												formData
											) => {
												formData["frontRearCode"] =
													selectedTargetCode;
												formData["materialCode"] =
													selectedItem?.materialCode;
												formData["materialCodeVer"] =
													selectedItem?.materialCodeVer;
											}}
											onSuccess={onSuccess}
										/>
									</AccessControl>
								</BasicCrudScreenActionItemLayout>
								<BasicCrudScreenActionItemLayout>
									<AccessControl
										accessTarget={schema && schema.name}
										accessMethod={
											schema &&
											(schema.updateWithPatch
												? "PATCH"
												: "PUT")
										}
									>
										<UpdateRecordButton
											id={table}
											schema={schema}
											grid={grid}
											refresh={api}
											disabled={!selectedItemColor}
											onSuccess={notifySuccess}
										/>
									</AccessControl>
								</BasicCrudScreenActionItemLayout>

								<BasicCrudScreenActionItemLayout>
									<AccessControl
										accessTarget={schema && schema.name}
										accessMethod="DELETE"
									>
										<DeleteSingleRecordButton
											id="ItemColor"
											disabled={disableDelButton}
											color="secondary"
											grid={grid}
											schema={schema}
											refresh={api}
											makeItemName={makeItemName}
											onSuccess={onSuccess}
											onFailure={onFailure}
										/>
									</AccessControl>
								</BasicCrudScreenActionItemLayout>
							</>
						)}
					/>
				</ReflexElement>
			</ReflexContainer>
		</div>
	);
}

export default ItemColor;
